// src/pages/Admin/NotificationList/NotificationList.jsx

import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  FaBell,
  FaCalendarAlt,
  FaClock,
  FaExclamationTriangle,
  FaSearch,
  FaSyncAlt,
  FaTimes,
  FaTrash,
} from "react-icons/fa";

import {
  getNotificationsByUser,
  deleteNotificationById,
} from "../../../services/notificationServices";

import { useNotification } from "../../../components/Notification/NotificationManager";
import { useUser } from "../../Home/UserContext";

import useScrollToTop from "../../../hooks/useScollToTop";

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

function getStoredUser() {
  try {
    return JSON.parse(
      localStorage.getItem("userInfo") || "null",
    );
  } catch (error) {
    return null;
  }
}

function getNotificationDate(notification) {
  const value =
    notification?.created_at ||
    notification?.createdAt ||
    notification?.date_creation ||
    null;

  if (!value) return null;

  const date = new Date(value);

  return Number.isNaN(date.getTime())
    ? null
    : date;
}

function formatDateTime(notification) {
  const date = getNotificationDate(notification);

  if (!date) return "Date inconnue";

  return date.toLocaleString("fr-FR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatShortDate(notification) {
  const date = getNotificationDate(notification);

  if (!date) return "—";

  return date.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function formatShortTime(notification) {
  const date = getNotificationDate(notification);

  if (!date) return "—";

  return date.toLocaleTimeString("fr-FR", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function isToday(notification) {
  const date = getNotificationDate(notification);

  if (!date) return false;

  const today = new Date();

  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
}

function isWithinLastDays(notification, days) {
  const date = getNotificationDate(notification);

  if (!date) return false;

  const now = new Date();
  const minimumDate = new Date(now);

  minimumDate.setDate(now.getDate() - days);
  minimumDate.setHours(0, 0, 0, 0);

  return date >= minimumDate;
}

function Card({ children, className }) {
  return (
    <div
      className={cn(
        "rounded-[24px] border border-white/80 bg-white/80",
        "shadow-[0_14px_40px_rgba(15,23,42,0.06)]",
        "backdrop-blur-xl",
        "dark:border-slate-800 dark:bg-slate-900/75",
        className,
      )}
    >
      {children}
    </div>
  );
}

function StatCard({
  label,
  value,
  helper,
  icon: Icon,
  tone = "blue",
}) {
  const tones = {
    blue: {
      border:
        "border-blue-200/80 dark:border-blue-500/20",
      icon:
        "bg-blue-500/10 text-blue-600 dark:text-blue-300",
      line:
        "from-blue-500/40 to-sky-400/20",
    },

    emerald: {
      border:
        "border-emerald-200/80 dark:border-emerald-500/20",
      icon:
        "bg-emerald-500/10 text-emerald-600 dark:text-emerald-300",
      line:
        "from-emerald-500/40 to-lime-400/20",
    },

    amber: {
      border:
        "border-amber-200/80 dark:border-amber-500/20",
      icon:
        "bg-amber-500/10 text-amber-600 dark:text-amber-300",
      line:
        "from-amber-500/40 to-orange-400/20",
    },
  };

  const selectedTone =
    tones[tone] || tones.blue;

  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-[22px] border bg-white/80 p-4",
        "shadow-[0_10px_30px_rgba(15,23,42,0.05)]",
        "dark:bg-slate-900/70",
        selectedTone.border,
      )}
    >
      <div
        className={cn(
          "absolute inset-x-0 top-0 h-1 bg-gradient-to-r",
          selectedTone.line,
        )}
      />

      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] font-black uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
            {label}
          </div>

          <div className="mt-2 text-3xl font-black leading-none text-slate-950 dark:text-white">
            {value}
          </div>

          <div className="mt-2 text-xs text-slate-500 dark:text-slate-400">
            {helper}
          </div>
        </div>

        <div
          className={cn(
            "flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl",
            selectedTone.icon,
          )}
        >
          <Icon />
        </div>
      </div>
    </div>
  );
}

function NotificationsSkeleton() {
  return (
    <div className="space-y-3 p-4">
      {Array.from({ length: 5 }).map(
        (_, index) => (
          <div
            key={index}
            className="h-16 animate-pulse rounded-2xl bg-slate-200/70 dark:bg-slate-800"
          />
        ),
      )}
    </div>
  );
}

function DeleteNotificationModal({
  notification,
  deleting,
  onClose,
  onConfirm,
}) {
  useEffect(() => {
    if (!notification) return undefined;

    const previousOverflow =
      document.body.style.overflow;

    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow =
        previousOverflow;
    };
  }, [notification]);

  if (!notification) return null;

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-slate-950/65 p-4 backdrop-blur-sm">
      <button
        type="button"
        aria-label="Fermer"
        className="absolute inset-0 cursor-default"
        onClick={
          deleting ? undefined : onClose
        }
      />

      <div className="relative z-10 w-full max-w-md overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-900">
        <header className="flex items-center justify-between border-b border-slate-200 px-5 py-4 dark:border-slate-800">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-rose-500/10 text-rose-600 dark:text-rose-300">
              <FaExclamationTriangle />
            </div>

            <div>
              <h3 className="font-black text-slate-900 dark:text-white">
                Supprimer la notification
              </h3>

              <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                Cette action est définitive
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={deleting}
            className="rounded-xl p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 disabled:opacity-50 dark:hover:bg-slate-800 dark:hover:text-white"
          >
            <FaTimes />
          </button>
        </header>

        <div className="p-5">
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950/50">
            <div className="line-clamp-4 text-sm leading-6 text-slate-700 dark:text-slate-300">
              {notification.message ||
                "Notification sans contenu"}
            </div>

            <div className="mt-3 flex items-center gap-2 text-xs font-medium text-slate-500 dark:text-slate-400">
              <FaCalendarAlt />
              {formatDateTime(notification)}
            </div>
          </div>
        </div>

        <footer className="flex flex-col-reverse gap-2 border-t border-slate-200 bg-slate-50 px-5 py-4 dark:border-slate-800 dark:bg-slate-900 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={deleting}
            className="rounded-xl border border-slate-300 bg-white px-4 py-2.5 font-semibold text-slate-700 transition hover:bg-slate-100 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
          >
            Annuler
          </button>

          <button
            type="button"
            onClick={onConfirm}
            disabled={deleting}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-rose-600 px-4 py-2.5 font-semibold text-white transition hover:bg-rose-500 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <FaTrash />

            {deleting
              ? "Suppression..."
              : "Supprimer"}
          </button>
        </footer>
      </div>
    </div>
  );
}

export default function NotificationList() {
  useScrollToTop();

  const { user } = useUser();
  const { addNotification } = useNotification();

  const storedUser = getStoredUser();
  const effectiveUser = user || storedUser;

  /*
   * Le fallback 1 conserve le comportement précédent.
   * Tu pourras le supprimer lorsque toutes les sessions
   * contiendront systématiquement l'identifiant utilisateur.
   */
  const userId =
    effectiveUser?.userId ||
    effectiveUser?.id ||
    1;

  const [notifications, setNotifications] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [refreshing, setRefreshing] =
    useState(false);

  const [deletingId, setDeletingId] =
    useState(null);

  const [
    notificationToDelete,
    setNotificationToDelete,
  ] = useState(null);

  const [search, setSearch] = useState("");
  const [periodFilter, setPeriodFilter] =
    useState("all");

  const [lastUpdated, setLastUpdated] =
    useState(null);

  const fetchNotifications = useCallback(
    async ({ refresh = false } = {}) => {
      if (refresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      try {
        const data =
          await getNotificationsByUser(userId);

        if (data === undefined || data === null) {
          throw new Error(
            "Aucune réponse reçue du serveur.",
          );
        }

        const notificationList = Array.isArray(data)
          ? data
          : Array.isArray(data?.notifications)
            ? data.notifications
            : [];

        setNotifications(notificationList);
        setLastUpdated(new Date());

        if (refresh) {
          addNotification(
            "Les notifications ont été actualisées.",
            "success",
          );
        }
      } catch (error) {
        console.error(
          "Erreur lors de la récupération des notifications :",
          error,
        );

        addNotification(
          "Erreur lors de la récupération des notifications.",
          "error",
        );
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [addNotification, userId],
  );

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const handleDeleteNotification =
    async () => {
      if (!notificationToDelete?.id) {
        return;
      }

      setDeletingId(notificationToDelete.id);

      try {
        await deleteNotificationById(
          notificationToDelete.id,
        );

        setNotifications(
          (currentNotifications) =>
            currentNotifications.filter(
              (notification) =>
                notification.id !==
                notificationToDelete.id,
            ),
        );

        addNotification(
          "Notification supprimée avec succès.",
          "success",
        );

        setNotificationToDelete(null);
      } catch (error) {
        console.error(
          "Erreur lors de la suppression de la notification :",
          error,
        );

        addNotification(
          "Erreur lors de la suppression de la notification.",
          "error",
        );
      } finally {
        setDeletingId(null);
      }
    };

  const sortedNotifications = useMemo(() => {
    return [...notifications].sort(
      (firstNotification, secondNotification) => {
        const firstDate = getNotificationDate(
          firstNotification,
        );

        const secondDate = getNotificationDate(
          secondNotification,
        );

        return (
          (secondDate?.getTime() || 0) -
          (firstDate?.getTime() || 0)
        );
      },
    );
  }, [notifications]);

  const filteredNotifications = useMemo(() => {
    const needle = search
      .trim()
      .toLowerCase();

    return sortedNotifications.filter(
      (notification) => {
        if (
          periodFilter === "today" &&
          !isToday(notification)
        ) {
          return false;
        }

        if (
          periodFilter === "7days" &&
          !isWithinLastDays(notification, 7)
        ) {
          return false;
        }

        if (
          periodFilter === "30days" &&
          !isWithinLastDays(notification, 30)
        ) {
          return false;
        }

        if (!needle) return true;

        const searchableContent = [
          notification.message,
          formatDateTime(notification),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        return searchableContent.includes(needle);
      },
    );
  }, [
    periodFilter,
    search,
    sortedNotifications,
  ]);

  const notificationStats = useMemo(() => {
    return {
      total: notifications.length,

      today: notifications.filter(isToday)
        .length,

      lastSevenDays: notifications.filter(
        (notification) =>
          isWithinLastDays(notification, 7),
      ).length,
    };
  }, [notifications]);

  return (
    <div className="w-full px-3 pb-12 pt-[116px] font-satoshi sm:px-5 lg:px-7">
      <div className="mx-auto max-w-[1500px]">
        {/* En-tête */}
        <header className="relative mb-7 overflow-hidden rounded-[28px] border border-white/70 bg-white/75 p-5 shadow-[0_18px_55px_rgba(15,23,42,0.08)] backdrop-blur-xl dark:border-slate-800 dark:bg-slate-900/75 sm:p-6">
          <div
            aria-hidden="true"
            className="absolute -right-20 -top-24 h-60 w-60 rounded-full bg-blue-500/10 blur-3xl"
          />

          <div
            aria-hidden="true"
            className="absolute -bottom-28 left-1/3 h-48 w-48 rounded-full bg-sky-400/10 blur-3xl"
          />

          <div className="relative flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-blue-200/70 bg-blue-50 px-3 py-1.5 text-[11px] font-black uppercase tracking-[0.16em] text-blue-700 dark:border-blue-500/20 dark:bg-blue-500/10 dark:text-blue-300">
                <span className="h-2 w-2 rounded-full bg-blue-500 shadow-[0_0_10px_rgba(59,130,246,0.7)]" />
                Communication
              </div>

              <h1 className="text-2xl font-black tracking-tight text-slate-950 dark:text-white sm:text-3xl lg:text-[34px]">
                Notifications
              </h1>

              <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600 dark:text-slate-400">
                Consultez et gérez les alertes envoyées
                à votre compte administrateur.
              </p>

              <div className="mt-4 inline-flex items-center gap-2 rounded-xl bg-slate-100/80 px-3 py-2 text-xs font-medium text-slate-500 dark:bg-slate-800/70 dark:text-slate-400">
                <span className="h-2 w-2 rounded-full bg-emerald-500" />

                {lastUpdated
                  ? `Actualisé le ${lastUpdated.toLocaleString("fr-FR")}`
                  : "Chargement des notifications..."}
              </div>
            </div>

            <button
              type="button"
              onClick={() =>
                fetchNotifications({
                  refresh: true,
                })
              }
              disabled={refreshing}
              className={cn(
                "inline-flex min-h-12 shrink-0 items-center justify-center gap-2 rounded-2xl px-5 py-3",
                "bg-gradient-to-r from-slate-950 to-slate-800",
                "font-bold text-white",
                "shadow-lg shadow-slate-950/15",
                "transition-all duration-200",
                "hover:-translate-y-0.5 hover:shadow-xl",
                "disabled:cursor-not-allowed disabled:opacity-60",
                "dark:from-blue-600 dark:to-sky-500",
              )}
            >
              <FaSyncAlt
                className={
                  refreshing
                    ? "animate-spin"
                    : ""
                }
              />

              {refreshing
                ? "Actualisation..."
                : "Rafraîchir"}
            </button>
          </div>
        </header>

        {/* Statistiques */}
        <section className="mb-5 grid grid-cols-1 gap-4 sm:grid-cols-3">
          <StatCard
            label="Total"
            value={notificationStats.total}
            helper="Toutes les notifications"
            icon={FaBell}
            tone="blue"
          />

          <StatCard
            label="Aujourd’hui"
            value={notificationStats.today}
            helper="Reçues depuis minuit"
            icon={FaClock}
            tone="emerald"
          />

          <StatCard
            label="7 derniers jours"
            value={
              notificationStats.lastSevenDays
            }
            helper="Activité récente"
            icon={FaCalendarAlt}
            tone="amber"
          />
        </section>

        {/* Filtres */}
        <Card className="mb-5 p-3 sm:p-4">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-[1fr_240px]">
            <div className="relative">
              <FaSearch className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />

              <input
                value={search}
                onChange={(event) =>
                  setSearch(event.target.value)
                }
                placeholder="Rechercher dans les notifications..."
                className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-sm text-slate-900 outline-none transition focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
              />
            </div>

            <select
              value={periodFilter}
              onChange={(event) =>
                setPeriodFilter(
                  event.target.value,
                )
              }
              className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-700 outline-none transition focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
            >
              <option value="all">
                Toutes les périodes
              </option>

              <option value="today">
                Aujourd’hui
              </option>

              <option value="7days">
                7 derniers jours
              </option>

              <option value="30days">
                30 derniers jours
              </option>
            </select>
          </div>
        </Card>

        {/* Liste */}
        <Card className="overflow-hidden">
          <div className="flex flex-col gap-2 border-b border-slate-200 px-4 py-4 dark:border-slate-800 sm:flex-row sm:items-center sm:justify-between sm:px-5">
            <div>
              <h2 className="font-black text-slate-900 dark:text-white">
                Historique des notifications
              </h2>

              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                {loading
                  ? "Chargement..."
                  : `${filteredNotifications.length} notification(s) affichée(s) sur ${notifications.length}`}
              </p>
            </div>
          </div>

          {loading ? (
            <NotificationsSkeleton />
          ) : filteredNotifications.length ===
            0 ? (
            <div className="px-5 py-16 text-center">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-[22px] bg-slate-100 text-2xl text-slate-400 dark:bg-slate-800 dark:text-slate-500">
                <FaBell />
              </div>

              <h3 className="mt-5 text-lg font-black text-slate-900 dark:text-white">
                Aucune notification
              </h3>

              <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-slate-500 dark:text-slate-400">
                Aucune notification ne correspond
                actuellement à la recherche ou à la
                période sélectionnée.
              </p>

              {search ||
              periodFilter !== "all" ? (
                <button
                  type="button"
                  onClick={() => {
                    setSearch("");
                    setPeriodFilter("all");
                  }}
                  className="mt-5 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800 dark:bg-white dark:text-slate-900"
                >
                  Réinitialiser les filtres
                </button>
              ) : null}
            </div>
          ) : (
            <>
              {/* Tableau desktop */}
              <div className="hidden overflow-x-auto md:block">
                <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-800">
                  <thead className="bg-slate-50/80 dark:bg-slate-950/40">
                    <tr>
                      <th className="w-16 px-5 py-3 text-left text-[10px] font-black uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
                        Type
                      </th>

                      <th className="px-5 py-3 text-left text-[10px] font-black uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
                        Message
                      </th>

                      <th className="w-48 px-5 py-3 text-left text-[10px] font-black uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
                        Date
                      </th>

                      <th className="w-28 px-5 py-3 text-right text-[10px] font-black uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
                        Action
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                    {filteredNotifications.map(
                      (notification) => (
                        <tr
                          key={notification.id}
                          className="transition hover:bg-blue-50/40 dark:hover:bg-slate-800/35"
                        >
                          <td className="px-5 py-4 align-top">
                            <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-blue-500/10 text-blue-600 dark:text-blue-300">
                              <FaBell />
                            </div>
                          </td>

                          <td className="px-5 py-4 align-top">
                            <div className="max-w-4xl whitespace-pre-wrap break-words text-sm font-medium leading-6 text-slate-700 dark:text-slate-200">
                              {notification.message ||
                                "Notification sans contenu"}
                            </div>
                          </td>

                          <td className="whitespace-nowrap px-5 py-4 align-top">
                            <div className="font-semibold text-slate-700 dark:text-slate-200">
                              {formatShortDate(
                                notification,
                              )}
                            </div>

                            <div className="mt-1 flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
                              <FaClock />
                              {formatShortTime(
                                notification,
                              )}
                            </div>
                          </td>

                          <td className="px-5 py-4 text-right align-top">
                            <button
                              type="button"
                              onClick={() =>
                                setNotificationToDelete(
                                  notification,
                                )
                              }
                              title="Supprimer la notification"
                              className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-rose-200 bg-rose-50 text-rose-600 transition hover:border-rose-300 hover:bg-rose-100 dark:border-rose-900/50 dark:bg-rose-950/30 dark:text-rose-300"
                            >
                              <FaTrash />
                            </button>
                          </td>
                        </tr>
                      ),
                    )}
                  </tbody>
                </table>
              </div>

              {/* Cartes mobile */}
              <div className="divide-y divide-slate-200 dark:divide-slate-800 md:hidden">
                {filteredNotifications.map(
                  (notification) => (
                    <article
                      key={notification.id}
                      className="p-4"
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-blue-500/10 text-blue-600 dark:text-blue-300">
                          <FaBell />
                        </div>

                        <div className="min-w-0 flex-1">
                          <div className="whitespace-pre-wrap break-words text-sm font-medium leading-6 text-slate-700 dark:text-slate-200">
                            {notification.message ||
                              "Notification sans contenu"}
                          </div>

                          <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
                            <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                              <FaCalendarAlt />

                              {formatDateTime(
                                notification,
                              )}
                            </div>

                            <button
                              type="button"
                              onClick={() =>
                                setNotificationToDelete(
                                  notification,
                                )
                              }
                              className="inline-flex items-center gap-2 rounded-xl bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-600 transition hover:bg-rose-100 dark:bg-rose-950/30 dark:text-rose-300"
                            >
                              <FaTrash />
                              Supprimer
                            </button>
                          </div>
                        </div>
                      </div>
                    </article>
                  ),
                )}
              </div>
            </>
          )}
        </Card>
      </div>

      <DeleteNotificationModal
        notification={notificationToDelete}
        deleting={
          deletingId ===
          notificationToDelete?.id
        }
        onClose={() => {
          if (!deletingId) {
            setNotificationToDelete(null);
          }
        }}
        onConfirm={
          handleDeleteNotification
        }
      />
    </div>
  );
}